import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import { toast } from "react-toastify";
import config from "@/lib/config";
import { ApiResponse } from "@/types/api";

interface RequestOptions {
  params?: Record<string, unknown>;
  skipAuth?: boolean;
  [key: string]: unknown;
}

class ApiClient {
  private instance: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: config.apiUrl,
      timeout: 10000,
      withCredentials: true, // 쿠키를 포함하여 요청
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 2xx 응답은 모두 성공으로 처리 (200, 201, 204 등)

        // 중첩된 응답 구조 처리 (success.data.data 형태)
        if (
          response.data &&
          typeof response.data === "object" &&
          response.data.success &&
          response.data.data &&
          typeof response.data.data === "object" &&
          response.data.data.success
        ) {
          // 중첩된 응답을 평탄화
          response.data = response.data.data;
        }

        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as typeof error.config & {
          _retry?: boolean;
        };

        // 401 Unauthorized인 경우 리프레시 토큰으로 재시도
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            if (originalRequest.url?.includes("/auth/refresh")) {
              throw new Error("Refresh token failed");
            }
            await this.refreshAccessToken();
            return this.instance(originalRequest);
          } catch (refreshError) {
            if (typeof window !== "undefined") {
              window.location.href = "/login";
            }
            return Promise.reject(refreshError);
          }
        }

        // 에러 메시지 처리
        const errorData = error.response?.data as
          { message?: string; error?: { message?: string } } | undefined;
        const errorMessage =
          errorData?.error?.message ||
          errorData?.message ||
          error.message ||
          "알 수 없는 오류가 발생했습니다.";

        // 인증 오류가 아닌 경우에만 토스트 표시
        if (typeof window !== "undefined" && error.response?.status !== 401) {
          toast.error(errorMessage);
        }

        return Promise.reject(error);
      },
    );
  }

  private refreshAccessToken(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = axios
        .post(`${config.apiUrl}/auth/refresh`, {}, { withCredentials: true })
        .then(() => undefined)
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  async get<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const { params, ...axiosOptions } = options;
    const response = await this.instance.get(endpoint, {
      params: params as Record<string, unknown>,
      ...axiosOptions,
    });

    // 중첩된 응답 처리 (success, data가 중첩된 경우)
    if (
      response.data &&
      typeof response.data === "object" &&
      "success" in response.data &&
      "data" in response.data
    ) {
      // 이미 표준화된 응답이면 그대로 반환
      return response.data;
    }

    return response.data;
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.post(endpoint, data, options);
    return response.data;
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.put(endpoint, data, options);
    return response.data;
  }

  async delete<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.delete(endpoint, options);
    return response.data;
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.patch(endpoint, data, options);
    return response.data;
  }

  async download(
    endpoint: string,
    data?: unknown,
    options: RequestOptions & {
      method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    } = { method: "POST" },
  ): Promise<{ blob: Blob; filename: string; contentType: string | null }> {
    const { method = "POST", ...axiosOptions } = options;

    const response = await this.instance.request({
      method,
      url: endpoint,
      data,
      responseType: "blob",
      ...axiosOptions,
    });

    const contentDisposition = response.headers["content-disposition"] || "";
    const contentType = response.headers["content-type"];

    // filename* (RFC 5987) 우선, 그 다음 filename="..." 또는 filename=...
    let filename = "download";
    const filenameStarMatch = contentDisposition.match(
      /filename\*=(?:UTF-8'')?([^;]+)/i,
    );
    if (filenameStarMatch && filenameStarMatch[1]) {
      try {
        filename = decodeURIComponent(filenameStarMatch[1].replace(/"/g, ""));
      } catch {
        filename = filenameStarMatch[1].replace(/"/g, "");
      }
    } else {
      const filenameMatchQuoted =
        contentDisposition.match(/filename="([^"]+)"/i);
      const filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
      if (filenameMatchQuoted && filenameMatchQuoted[1]) {
        filename = filenameMatchQuoted[1];
      } else if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/"/g, "");
      }
    }

    const blob = response.data;
    return { blob, filename, contentType };
  }
}

export const apiClient = new ApiClient();
